import { companyCreateSchema, DOC_IMAGE_MAX_BYTES, type CompanyDto, companyUpdateSchema, type CompanyUpdateInput, type MeDto, DEPOSIT_DOCUMENT_HINTS, formatMoney, SETTLEMENT_DOCUMENTS } from '@bqcrm/shared';
import { Button, Card, ColorInput, Drawer, FileButton, Group, NumberInput, Select, SimpleGrid, Stack, Switch, Text, Textarea, TextInput, Title } from '@mantine/core';
import { IconBuilding, IconPencil, IconPhoto, IconPlugConnected, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, apiForm } from '../api';
import { ask } from '../confirm';
import { can } from '../auth';
import { CompanyBadge } from '../components/CompanyBadge';
import { connectionBadge, FakturowniaSettings, useConnection } from '../components/FakturowniaSettings';
import { ingPayBadge, IngPaySettings, useIngPayConnection } from '../components/IngPaySettings';
import { ksefBadge, KsefSettings, useKsefConnection } from '../components/KsefSettings';
import { mailboxBadge, MailboxSettings, useMailbox } from '../components/MailboxSettings';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { EmptyState } from '../components/ui';
import { formatDateTime, formatPostalCode } from '../format';
import { usePostalCity } from '../postalCity';
import { notifyError, notifyOk } from '../notify';

function toForm(c: CompanyDto): CompanyUpdateInput {
  const { fullName, shortName, nip, regon, krs, street, postalCode, city, bankAccount, email, phone, color, settings } = c;
  return { fullName, shortName, nip, regon, krs, street, postalCode, city, bankAccount, email, phone, color, settings: { acceptanceOtp: true, offerTerms: '', ...settings, depositDocument: settings.depositDocument ?? 'zaliczkowa', finalDocument: settings.finalDocument ?? 'koncowa', contractThresholdGr: settings.contractThresholdGr ?? 0, eventPaymentDays: settings.eventPaymentDays ?? 7, paymentReminderDays: settings.paymentReminderDays ?? 0, completionNotice: settings.completionNotice ?? false, scheduleEmailScan: settings.scheduleEmailScan ?? false, offerExpiryNotice: settings.offerExpiryNotice ?? false, clientPortal: settings.clientPortal ?? true } };
}

const NEW_COMPANY: CompanyUpdateInput = {
  fullName: '',
  shortName: '',
  nip: '',
  regon: '',
  krs: '',
  street: '',
  postalCode: '',
  city: '',
  bankAccount: '',
  email: '',
  phone: '',
  color: '#0b7d62',
  settings: { offerValidityDays: 30, maxDiscountPct: 10, minMarginPct: 20, defaultDepositPct: 0, acceptanceOtp: true, offerTerms: '', depositDocument: 'zaliczkowa', finalDocument: 'koncowa', contractThresholdGr: 500000, eventPaymentDays: 7, paymentReminderDays: 0, completionNotice: false, scheduleEmailScan: false, offerExpiryNotice: false, clientPortal: true },
};

/** Formularz firmy — edycja istniejącej albo nowa firma (company = null). */
function CompanyForm({ company, onDone }: { company: CompanyDto | null; onDone: () => void }) {
  const [form, setForm] = useState<CompanyUpdateInput>(company ? toForm(company) : NEW_COMPANY);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (body: CompanyUpdateInput) =>
      company ? api<CompanyDto>(`/companies/${company.id}`, { method: 'PUT', body }) : api<CompanyDto>('/companies', { method: 'POST', body: { ...body, code } }),
    onSuccess: () => {
      notifyOk(company ? 'Zapisano dane firmy' : 'Dodano firmę — serie numeracji utworzono automatycznie');
      void qc.invalidateQueries({ queryKey: ['companies'] });
      onDone();
    },
    onError: notifyError,
  });

  const set = <K extends keyof CompanyUpdateInput>(k: K, v: CompanyUpdateInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setS = (k: keyof CompanyUpdateInput['settings'], v: number | string) =>
    setForm((f) => ({ ...f, settings: { ...f.settings, [k]: typeof v === 'number' ? v : Number(v) || 0 } }));
  const cityHint = usePostalCity(form.postalCode, form.city, (v) => set('city', v));
  const text = (k: keyof Omit<CompanyUpdateInput, 'settings'>, label: string) => (
    <TextInput label={label} value={form[k]} error={errors[k]} description={k === 'city' ? cityHint : undefined} onChange={(e) => set(k, e.currentTarget.value)} />
  );

  const submit = () => {
    const r = company ? companyUpdateSchema.safeParse(form) : companyCreateSchema.safeParse({ ...form, code });
    if (!r.success) {
      setErrors(Object.fromEntries(r.error.issues.map((i) => [String(i.path.join('.')), i.message])));
      return;
    }
    setErrors({});
    save.mutate(r.data);
  };

  return (
    <Stack>
      {company ? (
        <Text size="sm" c="dimmed">
          Skrót firmy „{company.code}” jest stały — występuje w numerach dokumentów.
        </Text>
      ) : (
        <TextInput
          label="Skrót firmy"
          description="1–4 wielkie litery lub cyfry, np. C. Występuje w numerach dokumentów i nie można go później zmienić."
          value={code}
          error={errors.code}
          maxLength={4}
          onChange={(e) => setCode(e.currentTarget.value.toUpperCase())}
        />
      )}
      {text('fullName', 'Pełna nazwa (na dokumentach)')}
      {text('shortName', 'Nazwa skrócona')}
      <SimpleGrid cols={{ base: 1, xs: 3 }}>
        {text('nip', 'NIP')}
        {text('regon', 'REGON')}
        {text('krs', 'KRS')}
      </SimpleGrid>
      {text('street', 'Ulica i numer')}
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <TextInput label="Kod pocztowy" placeholder="00-000" inputMode="numeric" maxLength={6} value={form.postalCode} error={errors.postalCode} onChange={(e) => set('postalCode', formatPostalCode(e.currentTarget.value))} />
        {text('city', 'Miejscowość')}
      </SimpleGrid>
      {text('bankAccount', 'Rachunek bankowy')}
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        {text('email', 'E-mail')}
        {text('phone', 'Telefon')}
      </SimpleGrid>
      <ColorInput label="Kolor w interfejsie" value={form.color} error={errors.color} onChange={(v) => set('color', v)} format="hex" />
      {company && <CompanyLogo company={company} />}
      <Text fw={700} size="sm" mt="sm">
        Ustawienia handlowe
      </Text>
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <NumberInput label="Ważność oferty (dni)" min={1} max={365} value={form.settings.offerValidityDays} onChange={(v) => setS('offerValidityDays', v)} />
        <NumberInput label="Domyślna zaliczka (%)" min={0} max={100} value={form.settings.defaultDepositPct} onChange={(v) => setS('defaultDepositPct', v)} />
        <NumberInput label="Maks. rabat bez zatwierdzenia (%)" min={0} max={100} value={form.settings.maxDiscountPct} onChange={(v) => setS('maxDiscountPct', v)} />
        <NumberInput label="Min. marża wariantu (%)" min={0} max={100} value={form.settings.minMarginPct} onChange={(v) => setS('minMarginPct', v)} />
      </SimpleGrid>
      <NumberInput
        label="Umowa od kwoty (brutto, zł)"
        description="Gdy suma zaakceptowanych ofert firmy w sprawie jest niższa — realizacja rusza od razu na podstawie oferty, bez umowy (zlecenie). 0 — zawsze umowa."
        min={0}
        step={500}
        thousandSeparator=" "
        decimalScale={0}
        value={Math.round((form.settings.contractThresholdGr ?? 0) / 100)}
        onChange={(v) => setForm((f) => ({ ...f, settings: { ...f.settings, contractThresholdGr: Math.max(0, Math.round(Number(v) || 0)) * 100 } }))}
      />
      <Text fw={700} size="sm" mt="sm">
        Rozliczenia (domyślne dla nowych umów)
      </Text>
      <Text size="xs" c="dimmed">
        Jaki dokument rozliczeniowy jest wymagany — według procesu przyjętego z księgowością. Można go zmienić przy każdej pozycji harmonogramu.
      </Text>
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <Select
          label="Zaliczka"
          description={DEPOSIT_DOCUMENT_HINTS[form.settings.depositDocument]}
          data={Object.entries(SETTLEMENT_DOCUMENTS).map(([value, label]) => ({ value, label }))}
          value={form.settings.depositDocument}
          allowDeselect={false}
          onChange={(v) => v && setForm((f) => ({ ...f, settings: { ...f.settings, depositDocument: v as 'zaliczkowa' } }))}
        />
        <Select
          label="Płatność końcowa"
          data={Object.entries(SETTLEMENT_DOCUMENTS).map(([value, label]) => ({ value, label }))}
          value={form.settings.finalDocument}
          allowDeselect={false}
          onChange={(v) => v && setForm((f) => ({ ...f, settings: { ...f.settings, finalDocument: v as 'koncowa' } }))}
        />
        <NumberInput
          label="Dni na zapłatę od zdarzenia"
          description="Płatność bez daty w umowie: termin = data zdarzenia (podpis, zakończenie prac, odbiór…) + tyle dni"
          min={0}
          max={120}
          value={form.settings.eventPaymentDays}
          onChange={(v) => setS('eventPaymentDays', v)}
        />
        <NumberInput
          label="Przypomnienie klientowi po terminie (dni)"
          description="E-mail do klienta z kwotą, terminem i rachunkiem, gdy płatność jest tyle dni po terminie — raz na płatność. 0 = wyłączone"
          min={0}
          max={60}
          value={form.settings.paymentReminderDays}
          onChange={(v) => setS('paymentReminderDays', v)}
        />
      </SimpleGrid>
      <Text fw={700} size="sm" mt="sm">
        Wiadomości do klienta
      </Text>
      <Switch
        label="Strefa klienta po podpisie umowy"
        description="Po podpisaniu umowy klient dostaje link: harmonogram prac w kalendarzu (czego dotyczy praca i kiedy się rozpocznie), umowy i oferty. Wejście kodem z e-maila"
        checked={form.settings.clientPortal !== false}
        onChange={(e) => {
          const v = e.currentTarget.checked;
          setForm((f) => ({ ...f, settings: { ...f.settings, clientPortal: v } }));
        }}
      />
      <Switch
        label="Skan umowy w e-mailu z harmonogramem płatności"
        description="Po podpisie papierowym klient dostaje skan podpisanej umowy (aneksu) w załączniku e-maila z harmonogramem"
        checked={form.settings.scheduleEmailScan === true}
        onChange={(e) => {
          const v = e.currentTarget.checked;
          setForm((f) => ({ ...f, settings: { ...f.settings, scheduleEmailScan: v } }));
        }}
      />
      <Switch
        label="Potwierdzenie zakończenia sprawy dla klienta"
        description="Po zamknięciu sprawy jako zrealizowanej klient dostaje e-mail: podziękowanie, informacja o gwarancji i kontakt do serwisu"
        checked={form.settings.completionNotice === true}
        onChange={(e) => {
          const v = e.currentTarget.checked;
          setForm((f) => ({ ...f, settings: { ...f.settings, completionNotice: v } }));
        }}
      />
      <Switch
        label="E-mail do klienta o wygaśnięciu oferty"
        description="Gdy przekazana klientowi oferta wygaśnie, a sprawa jest otwarta i nie ma nowszej wersji — klient dostaje e-mail „Oferta wygasła — przygotujemy aktualną wersję”"
        checked={form.settings.offerExpiryNotice === true}
        onChange={(e) => {
          const v = e.currentTarget.checked;
          setForm((f) => ({ ...f, settings: { ...f.settings, offerExpiryNotice: v } }));
        }}
      />
      <Switch
        label="Akceptacja oferty przez link wymaga kodu wysłanego e-mailem"
        checked={form.settings.acceptanceOtp !== false}
        onChange={(e) => {
          const v = e.currentTarget.checked;
          setForm((f) => ({ ...f, settings: { ...f.settings, acceptanceOtp: v } }));
        }}
      />
      <Textarea
        label="Domyślne warunki handlowe nowej oferty"
        autosize
        minRows={3}
        value={form.settings.offerTerms ?? ''}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setForm((f) => ({ ...f, settings: { ...f.settings, offerTerms: v } }));
        }}
      />
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onDone}>
          Anuluj
        </Button>
        <Button onClick={submit} loading={save.isPending}>
          {company ? 'Zapisz' : 'Dodaj firmę'}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Logo do dokumentów (docs/25): pole „Logo firmy” w szablonach i nagłówek układu wbudowanego oferty i protokołu.
 * Zapis od razu (poza formularzem); wydane dokumenty zachowują logo z chwili wydania.
 */
function CompanyLogo({ company }: { company: CompanyDto }) {
  const qc = useQueryClient();
  const [logoFileId, setLogoFileId] = useState(company.logoFileId);
  const resetFile = useRef<() => void>(null);
  const done = (c: CompanyDto, msg: string) => {
    setLogoFileId(c.logoFileId);
    notifyOk(msg);
    void qc.invalidateQueries({ queryKey: ['companies'] });
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      if (file.size > DOC_IMAGE_MAX_BYTES) throw new Error('Logo jest większe niż 1 MB — zmniejsz plik');
      const form = new FormData();
      form.append('file', file);
      return apiForm<CompanyDto>(`/companies/${company.id}/logo`, form);
    },
    onSuccess: (c) => done(c, 'Zapisano logo firmy'),
    onError: notifyError,
    onSettled: () => resetFile.current?.(),
  });
  const remove = useMutation({
    mutationFn: () => api<CompanyDto>(`/companies/${company.id}/logo`, { method: 'DELETE' }),
    onSuccess: (c) => done(c, 'Usunięto logo firmy'),
    onError: notifyError,
  });
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        Logo do dokumentów
      </Text>
      <Group align="center" wrap="wrap">
        {logoFileId ? (
          <img src={`/api/companies/${company.id}/logo?v=${logoFileId}`} alt="Logo firmy" style={{ maxHeight: 56, maxWidth: 200, border: '1px solid var(--app-line-strong)', borderRadius: 4, padding: 4, background: '#fff' }} />
        ) : (
          <Text size="sm" c="dimmed">
            Brak logo
          </Text>
        )}
        <FileButton onChange={(f) => f && upload.mutate(f)} accept="image/png,image/jpeg" resetRef={resetFile}>
          {(props) => (
            <Button {...props} size="xs" variant="light" leftSection={<IconPhoto size={14} />} loading={upload.isPending}>
              {logoFileId ? 'Zmień logo' : 'Wgraj logo'}
            </Button>
          )}
        </FileButton>
        {logoFileId && (
          <Button size="xs" variant="subtle" color="red" onClick={() => void ask('Nowe dokumenty będą bez logo. Wydane dokumenty się nie zmienią.', { title: 'Usunąć logo firmy?', confirm: 'Usuń', danger: true }).then((ok) => ok && remove.mutate())} loading={remove.isPending}>
            Usuń logo
          </Button>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        PNG albo JPG, do 1 MB (najlepiej PNG z przezroczystym tłem, ok. 600 px szerokości). W szablonie: przycisk „Logo i grafika” → „Wstaw logo firmy”. Wydane dokumenty zachowują dotychczasowe logo.
      </Text>
    </Stack>
  );
}

/** Stan integracji na karcie firmy (Fakturownia — sprzedaż, KSeF — zakupy, ING Pay — płatności online) + przyciski konfiguracji. */
function IntegrationLine({ company, onOpen, onKsef, onMailbox, onIng }: { company: CompanyDto; onOpen: () => void; onKsef: () => void; onMailbox: () => void; onIng: () => void }) {
  const q = useConnection(company.id);
  const k = useKsefConnection(company.id);
  const ing = useIngPayConnection(company.id);
  const m = useMailbox(company.id);
  return (
    <Stack gap={4} mt="sm">
      <Group justify="space-between">
        {connectionBadge(q.data, !!q.error)}
        <Button size="xs" variant="subtle" leftSection={<IconPlugConnected size={14} />} onClick={onOpen}>
          Integracja z Fakturownią
        </Button>
      </Group>
      <Group justify="space-between">
        {ksefBadge(k.data, !!k.error)}
        <Button size="xs" variant="subtle" leftSection={<IconPlugConnected size={14} />} onClick={onKsef}>
          Połączenie z KSeF
        </Button>
      </Group>
      <Group justify="space-between">
        {ingPayBadge(ing.data, !!ing.error)}
        <Button size="xs" variant="subtle" leftSection={<IconPlugConnected size={14} />} onClick={onIng}>
          Płatności online (ING Pay)
        </Button>
      </Group>
      <Group justify="space-between">
        {mailboxBadge(m.data, !!m.error)}
        <Button size="xs" variant="subtle" leftSection={<IconPlugConnected size={14} />} onClick={onMailbox}>
          Skrzynka odbiorcza
        </Button>
      </Group>
    </Stack>
  );
}

export function CompaniesPage({ me }: { me: MeDto }) {
  const q = useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyDto[]>('/companies') });
  const [editing, setEditing] = useState<CompanyDto | 'new' | null>(null);
  const canEdit = can(me, 'firmy.edycja');
  const canIntegrate = can(me, 'integracje.konfiguracja');
  const [integrating, setIntegrating] = useState<CompanyDto | null>(null);
  const [ksefFor, setKsefFor] = useState<CompanyDto | null>(null);
  const [mailboxFor, setMailboxFor] = useState<CompanyDto | null>(null);
  const [ingFor, setIngFor] = useState<CompanyDto | null>(null);

  return (
    <>
      <PageHeader
        title="Firmy"
        description={
          'Podmioty wystawiające dokumenty. Klienci, lokalizacje, katalog, pracownicy i kalendarz są wspólne; oferty, umowy, numeracja i płatności — osobne dla każdej firmy.' +
          (canEdit
            ? ' Nowa firma dostaje serie numeracji (do zmiany w „Numeracja”). Dostęp pracowników do firmy nadaje się w „Pracownicy → karta pracownika → Dane pracownika”; zakładający dostaje go od razu.'
            : '')
        }
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              Nowa firma
            </Button>
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data && q.data.length === 0 && (
          <Card>
            <EmptyState
              icon={<IconBuilding size={24} />}
              title="Brak firm"
              hint="Dodaj firmę, która wystawia oferty, umowy i faktury."
              action={
                canEdit && (
                  <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
                    Nowa firma
                  </Button>
                )
              }
            />
          </Card>
        )}
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {q.data?.map((c) => (
            <Card key={c.id} withBorder style={{ borderLeft: `4px solid ${c.color}` }}>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <CompanyBadge company={c} />
                  <Title order={4}>{c.shortName}</Title>
                </Group>
                {canEdit && (
                  <Button size="xs" variant="light" leftSection={<IconPencil size={14} />} onClick={() => setEditing(c)}>
                    Edytuj
                  </Button>
                )}
              </Group>
              <Text size="sm">{c.fullName}</Text>
              <Text size="sm" c="dimmed">
                {[c.street, `${c.postalCode} ${c.city}`.trim()].filter(Boolean).join(', ') || 'brak adresu'}
              </Text>
              <Text size="sm" c="dimmed">
                NIP: {c.nip || '—'} · REGON: {c.regon || '—'} {c.krs ? `· KRS: ${c.krs}` : ''}
              </Text>
              <Text size="sm" c="dimmed">
                {c.email || '—'} · {c.phone || '—'}
              </Text>
              <Text size="xs" c="dimmed" mt="sm">
                Ważność oferty {c.settings.offerValidityDays} dni · zaliczka domyślnie {c.settings.defaultDepositPct}% · rabat do {c.settings.maxDiscountPct}% · marża min.{' '}
                {c.settings.minMarginPct}% · umowa {c.settings.contractThresholdGr ? `od ${formatMoney(c.settings.contractThresholdGr)}` : 'zawsze'} · zmieniono {formatDateTime(c.updatedAt)}
              </Text>
              {canIntegrate && <IntegrationLine company={c} onOpen={() => setIntegrating(c)} onKsef={() => setKsefFor(c)} onMailbox={() => setMailboxFor(c)} onIng={() => setIngFor(c)} />}
            </Card>
          ))}
        </SimpleGrid>
      </QueryState>
      <Drawer opened={!!editing} onClose={() => setEditing(null)} title={editing === 'new' ? 'Nowa firma' : editing ? `Edycja: ${editing.shortName}` : ''} position="right" size="lg">
        {editing && <CompanyForm key={editing === 'new' ? 'new' : editing.id} company={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      </Drawer>
      <Drawer opened={!!integrating} onClose={() => setIntegrating(null)} title={integrating ? `Integracja z Fakturownią: ${integrating.shortName}` : ''} position="right" size="md">
        {integrating && <FakturowniaSettings key={integrating.id} company={integrating} />}
      </Drawer>
      <Drawer opened={!!ksefFor} onClose={() => setKsefFor(null)} title={ksefFor ? `Połączenie z KSeF: ${ksefFor.shortName}` : ''} position="right" size="md">
        {ksefFor && <KsefSettings key={ksefFor.id} company={ksefFor} />}
      </Drawer>
      <Drawer opened={!!ingFor} onClose={() => setIngFor(null)} title={ingFor ? `Płatności online ING Pay: ${ingFor.shortName}` : ''} position="right" size="md">
        {ingFor && <IngPaySettings key={ingFor.id} company={ingFor} />}
      </Drawer>
      <Drawer opened={!!mailboxFor} onClose={() => setMailboxFor(null)} title={mailboxFor ? `Skrzynka odbiorcza: ${mailboxFor.shortName}` : ''} position="right" size="md">
        {mailboxFor && <MailboxSettings key={mailboxFor.id} company={mailboxFor} />}
      </Drawer>
    </>
  );
}
